import { Component } from '@theme/component';
import { getWishlistItems, toggleWishlist, FcWishlistChangeEvent } from '@theme/fc-wishlist-api';

/**
 * Heart toggle button, placed on the PDP and product cards via
 * snippets/fc-wishlist-button.liquid. Works for every visitor — the list
 * lives in this browser's localStorage, no account required.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 * @extends {Component<Refs>}
 */
class FcWishlistButton extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(FcWishlistChangeEvent.eventName, this.#onChange);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(FcWishlistChangeEvent.eventName, this.#onChange);
  }

  async #sync() {
    const items = await getWishlistItems();
    this.#render(items.some((item) => item.handle === this.dataset.productHandle));
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const items = /** @type {CustomEvent} */ (event).detail;
    this.#render(items.some((item) => item.handle === this.dataset.productHandle));
  };

  /** @param {boolean} active */
  #render(active) {
    this.classList.toggle('is-wishlisted', active);
    this.refs.button.setAttribute('aria-pressed', String(active));
    this.refs.button.setAttribute('aria-label', active ? this.dataset.labelRemove : this.dataset.labelAdd);
  }

  /** @param {MouseEvent} event */
  async handleClick(event) {
    event.preventDefault();
    if (this.refs.button.disabled) return;

    this.refs.button.disabled = true;
    try {
      const variantId = Number(this.dataset.variantId) || null;
      await toggleWishlist(this.dataset.productHandle, variantId);
    } finally {
      this.refs.button.disabled = false;
    }
  }
}

if (!customElements.get('fc-wishlist-button')) customElements.define('fc-wishlist-button', FcWishlistButton);
